import {
  isClassified,
  normalizeClassifications,
  type BidAssociation,
  type Classifications,
  type Commitment,
  type DsmScopeItem,
  type ScopeItemInput
} from "design-scope";

/** Bulk form field groups. `undefined` means Mixed / not applying. */
export type BulkScopeForm = {
  responsibility: string | undefined;
  classification: Classifications | undefined;
  commitments: Commitment[] | undefined;
  bids: BidAssociation[] | undefined;
};

export type BulkScopeChanges = {
  responsibility: boolean;
  classification: boolean;
  commitments: boolean;
  bids: boolean;
};

function classificationEqual(
  a: Classifications | undefined,
  b: Classifications | undefined
): boolean {
  const na = normalizeClassifications(a) ?? null;
  const nb = normalizeClassifications(b) ?? null;
  return JSON.stringify(na) === JSON.stringify(nb);
}

function commitmentSignature(c: Commitment): string {
  return JSON.stringify({
    designPackageId: c.designPackageId,
    milestoneId: c.milestoneId,
    levelOfDetailValueId: c.levelOfDetailValueId,
    notes: c.notes.trim(),
    lod: c.lod
  });
}

function commitmentsEqual(
  a: Commitment[] | undefined,
  b: Commitment[] | undefined
): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  if (a.length !== b.length) return false;
  return a.every(
    (c, i) => commitmentSignature(c) === commitmentSignature(b[i]!)
  );
}

function bidSignature(b: BidAssociation): string {
  return JSON.stringify({
    bidPackageId: b.bidPackageId,
    primary: b.primary,
    role: b.role
  });
}

function bidsEqual(
  a: BidAssociation[] | undefined,
  b: BidAssociation[] | undefined
): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  if (a.length !== b.length) return false;
  return a.every((x, i) => bidSignature(x) === bidSignature(b[i]!));
}

function responsibilityEqual(
  a: string | undefined,
  b: string | undefined
): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  return a.trim() === b.trim();
}

function sameOrMixed<T>(
  values: T[],
  eq: (a: T, b: T) => boolean
): T | undefined {
  if (values.length === 0) return undefined;
  const first = values[0]!;
  return values.every((v) => eq(v, first)) ? first : undefined;
}

/** Snapshot shared vs Mixed field groups for the current selection. */
export function buildBulkBaseline(rows: DsmScopeItem[]): BulkScopeForm {
  if (rows.length === 0) {
    return {
      responsibility: undefined,
      classification: undefined,
      commitments: undefined,
      bids: undefined
    };
  }

  const responsibility = sameOrMixed(
    rows.map((r) => r.responsibility ?? ""),
    (a, b) => a.trim() === b.trim()
  );

  const classification = sameOrMixed(
    rows.map((r) => r.classification),
    classificationEqual
  );

  const commitments = sameOrMixed(
    rows.map((r) => r.commitments ?? []),
    commitmentsEqual
  );

  const bids = sameOrMixed(
    rows.map((r) => r.bids ?? []),
    bidsEqual
  );

  return {
    responsibility,
    classification:
      classification === undefined
        ? undefined
        : (normalizeClassifications(classification) ?? undefined),
    commitments: commitments === undefined ? undefined : [...commitments],
    bids: bids === undefined ? undefined : [...bids]
  };
}

/** Which draft field groups differ from the open-time baseline. */
export function diffBulkScopeForm(
  baseline: BulkScopeForm,
  draft: BulkScopeForm
): BulkScopeChanges {
  return {
    responsibility: !responsibilityEqual(
      baseline.responsibility,
      draft.responsibility
    ),
    classification: !classificationEqual(
      baseline.classification,
      draft.classification
    ),
    commitments: !commitmentsEqual(baseline.commitments, draft.commitments),
    bids: !bidsEqual(baseline.bids, draft.bids)
  };
}

export function hasBulkScopeChanges(changes: BulkScopeChanges): boolean {
  return (
    changes.responsibility ||
    changes.classification ||
    changes.commitments ||
    changes.bids
  );
}

/** Validate only field groups the user changed. */
export function validateBulkScopeDraft(
  draft: BulkScopeForm,
  changes: BulkScopeChanges,
  opts: { lodOptionsLoading?: boolean; lodOptionsCount: number }
): boolean {
  if (changes.classification && !isClassified(draft.classification)) {
    return false;
  }

  if (changes.commitments) {
    const pending = (draft.commitments ?? []).filter((c) => c.designPackageId);
    if (opts.lodOptionsLoading) return false;
    if (
      pending.length > 0 &&
      (opts.lodOptionsCount === 0 ||
        pending.some((c) => !c.levelOfDetailValueId))
    ) {
      return false;
    }
  }

  return true;
}

function filterCommitments(commitments: Commitment[]): Commitment[] {
  return commitments.filter((c) => c.designPackageId);
}

function filterBids(bids: BidAssociation[]): BidAssociation[] {
  return bids.filter((b) => b.bidPackageId);
}

/**
 * Build one ScopeItemInput for a selected row, overlaying only changed
 * bulk field groups. Title/Description always come from the row.
 */
export function mergeBulkScopeInput(
  row: DsmScopeItem,
  draft: BulkScopeForm,
  changes: BulkScopeChanges
): ScopeItemInput {
  return {
    title: row.title,
    description: row.description,
    status: row.status,
    notes: row.notes,
    responsibility: changes.responsibility
      ? (draft.responsibility ?? "").trim()
      : row.responsibility,
    classification: changes.classification
      ? draft.classification
      : row.classification,
    commitments: changes.commitments
      ? filterCommitments(draft.commitments ?? [])
      : row.commitments,
    bids: changes.bids ? filterBids(draft.bids ?? []) : row.bids
  };
}
